// Low-poly interpretations of Tollocan's sculpture gardens, assembled only at loading time.
export const addTollocanMonument = (THREE, parent, resources, roadY, z, variant, { stoneTexture } = {}) => {
  const group = new THREE.Group();
  group.name = 'tollocan-sculpture-garden';
  group.position.set(-13.7, roadY, z);
  const geometries = {
    block: new THREE.BoxGeometry(1, 1, 1),
    head: new THREE.IcosahedronGeometry(1, 1),
    disk: new THREE.CylinderGeometry(1, 1, 1, 12),
    ring: new THREE.TorusGeometry(1, 0.14, 4, 20),
    mound: new THREE.CylinderGeometry(0.45, 1, 1, 4, 1),
  };
  // The PixelLab map already contains dark stone; avoid multiplying it by another dark tint.
  const colors = { lava: 0xffffff, red: 0xffc4ae, pale: 0xfff2df, dark: 0xd9cec2, water: 0x447d80 };
  const stoneFill = { lava: 0x695f53, red: 0x956c5b, pale: 0xb2aaa0, dark: 0x665c51 };
  const materials = Object.fromEntries(Object.entries(colors).map(([key, color]) => [
    key,
    new THREE.MeshStandardMaterial({
      color,
      map: key === 'water' ? null : stoneTexture,
      // Gentle baked ambient fill keeps the unlit roadside faces readable without extra lights.
      emissive: stoneFill[key] || 0x000000,
      emissiveIntensity: 0.65,
      roughness: key === 'water' ? 0.35 : 1,
      flatShading: true,
    }),
  ]));
  resources.push(...Object.values(geometries), ...Object.values(materials));
  const batches = new Map();
  const pose = new THREE.Object3D();
  const piece = (shape, color, x, y, depth, sx, sy, sz, rx = 0, ry = 0, rz = 0) => {
    const key = `${shape}:${color}`;
    if (!batches.has(key)) batches.set(key, { shape, color, matrices: [] });
    pose.position.set(x, y, depth);
    pose.scale.set(sx, sy, sz);
    pose.rotation.set(rx, ry, rz);
    pose.updateMatrix();
    batches.get(key).matrices.push(pose.matrix.clone());
  };
  piece('block', 'lava', 0, 0.35, 0, 7.4, 0.7, 23);
  // Small irregular facing stones break up the retaining wall without texture downloads.
  for (let i = 0; i < 30; i += 1) {
    piece('block', i % 3 ? 'dark' : 'pale', 3.73, 0.22 + (i % 2) * 0.23,
      -10.9 + i * 0.75, 0.08, 0.2, 0.54 + (i % 3) * 0.07);
  }
  const seatedFigure = (depth, color, scale = 1) => {
    // Broad seats, upright torsos, faceted heads and projecting knees face the roadway.
    piece('block', color, 0, 1.05, depth, 2.5, 0.65, 1.8);
    piece('block', color, -0.65, 2.3, depth, 0.75, 2.5 * scale, 1.6);
    piece('block', color, 0, 2.25, depth, 0.9, 1.6 * scale, 0.85);
    piece('head', color, 0.12, 3.45 * scale, depth, 0.48, 0.6, 0.48);
    piece('block', color, 0.57, 3.4 * scale, depth, 0.18, 0.17, 0.23);
    [-0.48, 0.48].forEach((side) => {
      piece('block', color, 0.5, 1.6, depth + side, 1.55, 0.52, 0.48);
      piece('block', color, 1.03, 1.1, depth + side, 0.48, 0.95, 0.5);
      piece('block', color, 0.36, 2.15, depth + side, 1.15, 0.36, 0.34);
    });
  };
  if (variant === 0) {
    piece('mound', 'lava', -0.8, 1.6, -7.2, 3.1, 2.4, 4.3, 0, Math.PI / 4);
    seatedFigure(-3.7, 'red', 1.05);
    seatedFigure(0, 'red');
    seatedFigure(6.4, 'pale', 0.9);
    piece('block', 'dark', 0.3, 1.1, 3.3, 2.8, 0.9, 1.9);
    piece('head', 'dark', 0.1, 2.0, 3.3, 0.95, 1.05, 0.7);
    // Upright solar disk on the raised rear platform, facing the passing player.
    piece('block', 'red', -0.7, 3.4, -7.2, 0.35, 2, 0.35);
    piece('disk', 'red', -0.7, 5, -7.2, 1.1, 0.3, 1.1, 0, 0, Math.PI / 2);
    for (let ray = 0; ray < 10; ray += 1) {
      const angle = ray * Math.PI / 5;
      piece('block', 'dark', -0.7, 5 + Math.cos(angle) * 1.55, -7.2 + Math.sin(angle) * 1.55,
        0.2, 0.9, 0.2, angle);
    }
  } else {
    [-7, -0.7, 6.3].forEach((depth, index) => {
      // Truncated earthen pyramids with pale circular stone crests.
      piece('mound', 'lava', -0.5, 1.6, depth, 3.3, 2.5, 3.5, 0, Math.PI / 4);
      piece('disk', index === 1 ? 'red' : 'pale', 0, 3.3, depth,
        0.9, 0.48, 1.05, 0, 0, Math.PI / 2);
      piece('block', 'dark', 0.27, 3.25, depth, 0.08, 0.12, 0.65);
    });
    // Shallow turquoise basin and a stone rim, set in the front terrace.
    piece('disk', 'water', 2, 0.75, 3.1, 1.03, 0.08, 1.03);
    piece('ring', 'pale', 2, 0.83, 3.1, 1.18, 1.18, 1, -Math.PI / 2);
  }
  batches.forEach(({ shape, color, matrices }) => {
    const mesh = new THREE.InstancedMesh(geometries[shape], materials[color], matrices.length);
    matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.frustumCulled = false;
    group.add(mesh);
    resources.push(mesh);
  });
  group.traverse((node) => {
    node.updateMatrix();
    node.matrixAutoUpdate = false;
  });
  parent.add(group);
};
